import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getHyperliquidMonitorAlerts, getHyperliquidMonitorHealth, getHyperliquidAuction, getHyperliquidDeployFeed } from '../api';
import { useIsMobile } from '../hooks/useIsMobile';

const MARKET_LABEL = { perp: 'Perp', spot: 'Spot' };

function fmtTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function fmtDuration(t, sec) {
  if (sec == null || !Number.isFinite(sec)) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return t('hyperliquidMonitor.durationHourMin', { h, m });
  const s = Math.floor(sec % 60);
  return t('hyperliquidMonitor.durationMinSec', { m, s });
}

const perpTradeUrl = (name) => `https://app.hyperliquid.xyz/trade/${encodeURIComponent(name)}`;

export default function HyperliquidMonitorPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [alerts, setAlerts] = useState([]);
  const [deployFeed, setDeployFeed] = useState([]);
  const [health, setHealth] = useState(null);
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marketFilter, setMarketFilter] = useState('');
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    try {
      const [{ alerts }, { alerts: feed }, health, auction] = await Promise.all([
        getHyperliquidMonitorAlerts({ days: 30, marketType: marketFilter || undefined }),
        getHyperliquidDeployFeed({ days: 30 }).catch(() => ({ alerts: [] })),
        getHyperliquidMonitorHealth().catch(() => null),
        getHyperliquidAuction().catch(() => null),
      ]);
      setAlerts(alerts || []);
      setDeployFeed(feed || []);
      setHealth(health);
      setAuction(auction);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [marketFilter]);

  // 5s poll for alerts/health (matches the backend's own detection cadence
  // closely enough); the auction price itself ticks visually every second
  // via the `now` clock below so the countdown feels live between fetches.
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const healthy = health && health.lastPollAt && (Date.now() - new Date(health.lastPollAt).getTime()) < 60000;
  const healthColor = !health ? 'var(--text-dim)' : health.lastError ? '#dc2626' : healthy ? '#10b981' : '#f59e0b';
  const healthLabel = !health ? t('hyperliquidMonitor.healthDisconnected') : health.lastError ? t('hyperliquidMonitor.healthError', { error: health.lastError }) : healthy ? t('hyperliquidMonitor.healthRunning') : t('hyperliquidMonitor.healthStale');

  const pageStyle = isMobile ? { ...s.page, padding: '16px 12px' } : s.page;

  return (
    <div style={pageStyle}>
      <div style={s.header}>
        <div>
          <h2 style={s.title}>{t('hyperliquidMonitor.title')}</h2>
          <p style={s.subtitle}>
            {t('hyperliquidMonitor.subtitle')}
          </p>
        </div>
      </div>

      <div style={s.healthRow}>
        <div style={s.chip}>
          <span style={{ ...s.dot, background: healthColor }} />
          <span style={{ fontWeight: 700, color: 'var(--text)' }}>{t('hyperliquidMonitor.monitorLabel')}</span>
          <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{healthLabel}</span>
        </div>
        {health && health.deployFeed && (
          <div style={s.chip}>
            <span style={{ ...s.dot, background: health.deployFeed.enabled ? (health.deployFeed.lastError ? '#dc2626' : '#10b981') : 'var(--text-dim)' }} />
            <span style={{ fontWeight: 700, color: 'var(--text)' }}>{t('hyperliquidMonitor.deployFeedLabel')}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
              {!health.deployFeed.enabled ? t('hyperliquidMonitor.healthOff') : health.deployFeed.lastError ? t('hyperliquidMonitor.healthError', { error: health.deployFeed.lastError }) : t('hyperliquidMonitor.healthRunning')}
            </span>
          </div>
        )}
      </div>

      <div style={s.auctionRow}>
        <AuctionCard title={t('hyperliquidMonitor.auctionPerpTitle')} auction={auction && auction.perp} now={now} />
        <AuctionCard title={t('hyperliquidMonitor.auctionSpotTitle')} auction={auction && auction.spot} now={now} />
      </div>

      <DeployFeedSection deployFeed={deployFeed} isMobile={isMobile} />

      <div style={s.filters}>
        <div style={s.filterGroup}>
          <span style={s.filterLabel}>{t('hyperliquidMonitor.filterMarketType')}</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[{ value: '', label: t('hyperliquidMonitor.filterAll') }, { value: 'perp', label: 'Perp' }, { value: 'spot', label: 'Spot' }].map((o) => (
              <button key={o.value} onClick={(e) => { setMarketFilter(o.value); e.currentTarget.blur(); }}
                style={marketFilter === o.value ? s.filterBtnActive : s.filterBtn}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={s.loading}>{t('hyperliquidMonitor.loadingData')}</div>
      ) : alerts.length === 0 ? (
        <div style={s.empty}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>⚡</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
            {t('hyperliquidMonitor.emptyTitle')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 520, textAlign: 'center' }}>
            {t('hyperliquidMonitor.emptyDesc')}
          </div>
        </div>
      ) : isMobile ? (
        <div style={s.cards}>
          {alerts.map((a) => <AlertCard key={a._id} a={a} />)}
        </div>
      ) : (
        <div style={s.tableWrap}>
          <div style={s.thead}>
            <span style={{ ...s.th, flex: 1 }}>{t('hyperliquidMonitor.colType')}</span>
            <span style={{ ...s.th, flex: 1.4 }}>{t('hyperliquidMonitor.colMarketName')}</span>
            <span style={{ ...s.th, flex: 1.2 }}>{t('hyperliquidMonitor.colDetectedAt')}</span>
            <span style={{ ...s.th, flex: 1.8 }}>{t('hyperliquidMonitor.colOtherCex')}</span>
          </div>
          {alerts.map((a) => <AlertRow key={a._id} a={a} />)}
        </div>
      )}
    </div>
  );
}

function AuctionCard({ title, auction, now }) {
  const { t } = useTranslation();
  if (!auction) {
    return (
      <div style={s.statsCard}>
        <div style={s.statsHeader}><span style={{ fontWeight: 800, color: 'var(--text)', fontSize: 14 }}>{title}</span></div>
        <p style={{ ...s.statsNote, borderTop: 'none', paddingTop: 0 }}>{t('hyperliquidMonitor.loadingEllipsis')}</p>
      </div>
    );
  }
  const startMs = auction.startTimeSeconds * 1000;
  const endMs = startMs + auction.durationSeconds * 1000;
  const remainingSec = Math.max(0, Math.round((endMs - now) / 1000));
  const startGas = parseFloat(auction.startGas);
  // Once someone accepts the price, HyperLiquid stops reporting currentGas
  // (comes back null) and endGas gets set instead — that's the "resolved"
  // state, not a data error, so show the final price it closed at rather
  // than a bare NaN.
  const justWon = auction.endGas != null;
  const currentGas = justWon ? parseFloat(auction.endGas) : parseFloat(auction.currentGas);
  const pctElapsed = justWon || !(startGas > 0) ? 100 : Math.min(100, Math.max(0, ((startGas - currentGas) / startGas) * 100));

  return (
    <div style={s.statsCard}>
      <div style={s.statsHeader}>
        <span style={{ fontWeight: 800, color: 'var(--text)', fontSize: 14 }}>{title}</span>
        {justWon && <span style={{ ...s.badge, color: '#f59e0b', borderColor: '#f59e0b55', background: '#f59e0b14', marginLeft: 'auto' }}>{t('hyperliquidMonitor.justSettled')}</span>}
      </div>
      <div style={s.statsGrid}>
        <Cell label={justWon ? t('hyperliquidMonitor.settledPrice') : t('hyperliquidMonitor.currentPrice')} value={Number.isFinite(currentGas) ? currentGas.toLocaleString('en-US', { maximumFractionDigits: 4 }) : '—'} color="#f59e0b" />
        <Cell label={t('hyperliquidMonitor.startPrice')} value={Number.isFinite(startGas) ? startGas.toLocaleString('en-US', { maximumFractionDigits: 4 }) : '—'} />
        <Cell label={t('hyperliquidMonitor.statusLabel')} value={justWon ? t('hyperliquidMonitor.statusSettled') : (remainingSec > 0 ? fmtDuration(t, remainingSec) : t('hyperliquidMonitor.statusTimeUp'))} />
      </div>
      <div style={s.progressTrack}>
        <div style={{ ...s.progressFill, width: `${pctElapsed}%` }} />
      </div>
      <p style={s.statsNote}>
        {justWon
          ? t('hyperliquidMonitor.auctionSettledNote')
          : t('hyperliquidMonitor.auctionRunningNote')}
      </p>
    </div>
  );
}

const explorerUserUrl = (addr) => `https://hypurrscan.io/address/${addr}`;

// Whether the base asset is already listed on Binance/OKX — exclusive to
// Aster/HyperLiquid before a major CEX picks it up is the stronger signal.
function CrossListingBadges({ crossListing }) {
  const { t } = useTranslation();
  if (!crossListing) return <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>—</span>;
  const hits = [
    crossListing.binance?.spot && 'Binance Spot',
    crossListing.binance?.futures && 'Binance Futures',
    crossListing.okx?.spot && 'OKX Spot',
    crossListing.okx?.swap && 'OKX Swap',
  ].filter(Boolean);
  if (hits.length === 0) {
    return <span style={{ ...s.sourceTag, color: '#10b981', borderColor: '#10b98155', background: '#10b98114' }}>{t('hyperliquidMonitor.notOnCex')}</span>;
  }
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {hits.map((h) => (
        <span key={h} style={{ ...s.sourceTag, color: '#f59e0b', borderColor: '#f59e0b55', background: '#f59e0b14' }}>{h}</span>
      ))}
    </div>
  );
}

// Earliest possible signal — a deployer just registered a ticker name
// (via the third-party HypurrScan feed), typically ~10-20s before it's
// actually tradeable. Deliberately shown as its own "unconfirmed" section
// rather than merged into the alerts table below: registering a name is
// not a guarantee the deployer finishes genesis/registerSpot afterward.
function DeployFeedSection({ deployFeed, isMobile }) {
  const { t } = useTranslation();
  if (!deployFeed || deployFeed.length === 0) return null;
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={s.sectionHeader}>
        <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)' }}>{t('hyperliquidMonitor.deployFeedTitle')}</span>
      </div>
      {isMobile ? (
        <div style={s.cards}>
          {deployFeed.slice(0, 10).map((f) => <DeployFeedCard key={f._id} f={f} />)}
        </div>
      ) : (
        <div style={s.tableWrap}>
          <div style={s.thead}>
            <span style={{ ...s.th, flex: 1.3 }}>{t('hyperliquidMonitor.colTicker')}</span>
            <span style={{ ...s.th, flex: 1.8 }}>{t('hyperliquidMonitor.colFullName')}</span>
            <span style={{ ...s.th, flex: 1.2 }}>{t('hyperliquidMonitor.colRegisteredAt')}</span>
            <span style={{ ...s.th, flex: 1.4 }}>{t('hyperliquidMonitor.colDeployerWallet')}</span>
            <span style={{ ...s.th, flex: 1.8 }}>{t('hyperliquidMonitor.colOtherCex')}</span>
          </div>
          {deployFeed.slice(0, 10).map((f) => (
            <div key={f._id} style={s.row}>
              <span style={{ ...s.td, flex: 1.3, fontWeight: 700, color: 'var(--text)' }}>{f.ticker}</span>
              <span style={{ ...s.td, flex: 1.8 }}>{f.fullName || '—'}</span>
              <span style={{ ...s.td, flex: 1.2 }}>{fmtTime(f.registeredAt)}</span>
              <span style={{ ...s.td, flex: 1.4 }}>
                <a href={explorerUserUrl(f.user)} target="_blank" rel="noopener noreferrer" style={s.txLink} title={f.user}>
                  {f.user.slice(0, 6)}…{f.user.slice(-4)} ↗
                </a>
              </span>
              <span style={{ ...s.td, flex: 1.8 }}><CrossListingBadges crossListing={f.crossListing} /></span>
            </div>
          ))}
          <p style={s.note}>{t('hyperliquidMonitor.deployFeedNote')}</p>
        </div>
      )}
    </div>
  );
}

function DeployFeedCard({ f }) {
  const { t } = useTranslation();
  return (
    <div style={s.card}>
      <div style={s.cardTop}>
        <span style={s.cardName}>{f.ticker}</span>
        <span style={{ ...s.badge, color: '#22d3ee', borderColor: '#22d3ee55', background: '#22d3ee14' }}>{t('hyperliquidMonitor.justRegistered')}</span>
      </div>
      <Cell label={t('hyperliquidMonitor.cardFullName')} value={f.fullName || '—'} />
      <Cell label={t('hyperliquidMonitor.cardRegisteredAt')} value={fmtTime(f.registeredAt)} />
      <CrossListingBadges crossListing={f.crossListing} />
      <a href={explorerUserUrl(f.user)} target="_blank" rel="noopener noreferrer" style={{ ...s.cardMeta, ...s.txLink }} title={f.user}>
        {t('hyperliquidMonitor.cardWallet')} {f.user.slice(0, 6)}…{f.user.slice(-4)} ↗
      </a>
    </div>
  );
}

// Spot pairs are traded under their raw "@<index>" coin id, not the human
// name — so when the two differ, show both: the ticker as the headline,
// the tradeable id as a small secondary hint.
function displayLabel(a) {
  return a.displayName && a.displayName !== a.name ? a.displayName : a.name;
}

function AlertRow({ a }) {
  const label = displayLabel(a);
  return (
    <div style={s.row}>
      <span style={{ ...s.td, flex: 1 }}>{MARKET_LABEL[a.marketType] || a.marketType}</span>
      <span style={{ ...s.td, flex: 1.4, fontWeight: 700, color: 'var(--text)' }}>
        {a.marketType === 'perp' ? (
          <a href={perpTradeUrl(a.name)} target="_blank" rel="noopener noreferrer" style={s.txLink}>{label} ↗</a>
        ) : label}
        {label !== a.name && <span style={s.subLabel}> ({a.name})</span>}
      </span>
      <span style={{ ...s.td, flex: 1.2 }}>{fmtTime(a.detectedAt)}</span>
      <span style={{ ...s.td, flex: 1.8 }}><CrossListingBadges crossListing={a.crossListing} /></span>
    </div>
  );
}

function AlertCard({ a }) {
  const { t } = useTranslation();
  const label = displayLabel(a);
  return (
    <div style={s.card}>
      <div style={s.cardTop}>
        <span style={s.cardName}>
          {a.marketType === 'perp' ? (
            <a href={perpTradeUrl(a.name)} target="_blank" rel="noopener noreferrer" style={s.txLink}>{label} ↗</a>
          ) : label}
          {label !== a.name && <span style={s.subLabel}> ({a.name})</span>}
        </span>
        <span style={{ ...s.badge, color: 'var(--accent)', borderColor: 'var(--accent)55', background: 'var(--accent)14' }}>
          {MARKET_LABEL[a.marketType] || a.marketType}
        </span>
      </div>
      <Cell label={t('hyperliquidMonitor.cardDetectedAt')} value={fmtTime(a.detectedAt)} />
      <CrossListingBadges crossListing={a.crossListing} />
    </div>
  );
}

function Cell({ label, value, color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: color || 'var(--text)', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}

const s = {
  page: { padding: '24px 32px', maxWidth: 1200, margin: '0 auto' },
  header: { marginBottom: 20 },
  title: { margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: 'var(--text)' },
  subtitle: { margin: 0, fontSize: 13, color: 'var(--text-muted)' },
  loading: { color: 'var(--text-muted)', textAlign: 'center', padding: 60, fontSize: 14 },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '70px 20px' },

  healthRow: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 },
  chip: { display: 'flex', alignItems: 'center', gap: 6, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '6px 12px', fontSize: 12 },
  dot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },

  auctionRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 20 },
  sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 8 },
  statsCard: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '16px 18px', minWidth: 0 },
  statsHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 12, marginBottom: 10 },
  statsNote: { fontSize: 10.5, color: 'var(--text-dim)', lineHeight: 1.6, margin: 0, borderTop: '1px solid var(--border)', paddingTop: 10 },

  progressTrack: { height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden', marginBottom: 10 },
  progressFill: { height: '100%', background: 'linear-gradient(90deg, #f59e0b, #fbbf24)', transition: 'width 1s linear' },

  filters: { display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 20 },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  filterLabel: { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 },
  filterBtn: {
    fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', background: 'var(--surface)',
    outline: 'none', boxShadow: 'none',
    borderWidth: 1, borderStyle: 'solid', borderColor: 'var(--border)',
    borderRadius: 8, padding: '5px 10px', cursor: 'pointer',
  },
  filterBtnActive: {
    fontSize: 11, fontWeight: 600, color: 'var(--accent)', background: 'var(--accent)14',
    outline: 'none', boxShadow: 'none',
    borderWidth: 1, borderStyle: 'solid', borderColor: 'var(--accent)',
    borderRadius: 8, padding: '5px 10px', cursor: 'pointer',
  },

  tableWrap: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '8px 14px 14px', overflowX: 'auto' },
  thead: { display: 'flex', gap: 12, padding: '10px 8px', borderBottom: '1px solid var(--border)', marginBottom: 4 },
  th: { flex: 1, fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 0.7, textTransform: 'uppercase', minWidth: 0 },
  row: { display: 'flex', gap: 12, alignItems: 'center', padding: '11px 8px', borderBottom: '1px solid var(--border)', minWidth: 0 },
  td: { flex: 1, fontSize: 12, color: 'var(--text-dim)', minWidth: 0, wordBreak: 'break-word', overflowWrap: 'break-word' },
  badge: { fontSize: 10, fontWeight: 700, border: '1px solid', borderRadius: 8, padding: '3px 9px', whiteSpace: 'nowrap', display: 'inline-block' },
  sourceTag: { fontSize: 9, fontWeight: 600, border: '1px solid', borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap', display: 'inline-block' },
  subLabel: { fontSize: 10, fontWeight: 500, color: 'var(--text-dim)' },
  note: { fontSize: 10, color: 'var(--text-dim)', margin: '10px 0 0' },

  cards: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' },
  cardName: { fontSize: 14, fontWeight: 700, color: 'var(--text)', wordBreak: 'break-word' },
  cardMeta: { fontSize: 10, color: 'var(--text-dim)' },
  txLink: { color: 'var(--accent)', textDecoration: 'none', display: 'inline-block' },
};
